/**
 * THEME-1 · 后台读主题令牌：把后端算好的令牌（规范名 → 值）写成 <html> 上的 CSS 变量。
 *
 * · tailwind.config.ts 的 blue-* / brand / accent 读 `--c-*`（空格分隔 R G B，配合 rgb(var(--x) / <alpha-value>)）；
 *   本文件只负责「写」，不做任何颜色计算（令牌派生只在后端，前端不算）。
 * · classic 且没改色 = 令牌为空 → 什么都不写（并清掉之前写过的），后台和改前一模一样。
 * · 上次的令牌 + 后台版式缓存在 localStorage：下次打开页面在首帧前就按缓存换好色 / 选好版式，不闪一下经典蓝。
 */
export type ThemeTokens = Record<string, string>;
export type AdminLayout = "sidebar" | "topnav";

const CACHE_KEY = "ota-theme-v1";

/** 令牌名 → 后台 Tailwind 读的变量名（blue-N / slate-N 色阶 / paper / brand / accent）。其余令牌（paper / ink …）写成 --t-<名>，给新写的页面 / 预览块用。 */
const TAILWIND_VARS: Record<string, string> = {
  brand: "--c-brand", "brand-hover": "--c-brand-hover", "brand-soft": "--c-brand-soft",
  accent: "--c-accent", "accent-hover": "--c-accent-hover", "accent-soft": "--c-accent-soft",
};
for (const n of [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950]) {
  TAILWIND_VARS[`blue-${n}`] = `--c-blue-${n}`;
  TAILWIND_VARS[`slate-${n}`] = `--c-slate-${n}`;   // THEME-1b · pine 带 slate-N（后台中性色）
}
TAILWIND_VARS.paper = "--c-paper";                  // THEME-1b · 页面底色

let applied: string[] = [];

/** #RRGGBB → "R G B"（写给 Tailwind 的 rgb(var(--x) / <alpha-value>)）；不是颜色返回 null。 */
export function hexToTriplet(hex: string): string | null {
  const m = /^#?([0-9a-fA-F]{6})$/.exec((hex ?? "").trim());
  if (!m) return null;
  const n = m[1];
  return `${parseInt(n.slice(0, 2), 16)} ${parseInt(n.slice(2, 4), 16)} ${parseInt(n.slice(4, 6), 16)}`;
}

export function clearTheme(el: HTMLElement = document.documentElement): void {
  for (const name of applied) el.style.removeProperty(name);
  applied = [];
}

/** 把令牌写到 <html>；先清掉上一次写的，所以 classic（空令牌）= 回到 index.css 里的默认值。 */
export function applyTheme(tokens: ThemeTokens | null | undefined, el: HTMLElement = document.documentElement): void {
  clearTheme(el);
  if (!tokens) return;
  for (const [key, value] of Object.entries(tokens)) {
    const tw = TAILWIND_VARS[key];
    if (tw) {
      const t = hexToTriplet(value);
      if (t) {
        el.style.setProperty(tw, t);
        applied.push(tw);
      }
    }
    const raw = `--t-${key}`;
    el.style.setProperty(raw, value);
    applied.push(raw);
  }
}

/** 预览块用：令牌 → 内联样式对象（只作用在块里，不动整个后台）。 */
export function tokensToStyle(tokens: ThemeTokens | null | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(tokens ?? {})) {
    const tw = TAILWIND_VARS[key];
    const t = tw ? hexToTriplet(value) : null;
    if (tw && t) out[tw] = t;
    out[`--t-${key}`] = value;
  }
  return out;
}

export interface ThemeCache {
  tokens: ThemeTokens;
  admin_layout: AdminLayout;
}

export function readCache(): ThemeCache {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) {
      const j = JSON.parse(raw) as Partial<ThemeCache>;
      return { tokens: j.tokens && typeof j.tokens === "object" ? j.tokens : {}, admin_layout: j.admin_layout === "topnav" ? "topnav" : "sidebar" };
    }
  } catch {
    /* 隐私模式 / 坏数据：当没有缓存 */
  }
  return { tokens: {}, admin_layout: "sidebar" };
}

export function writeCache(c: ThemeCache): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(c));
  } catch {
    /* ignore */
  }
}

/** 应用启动时（渲染前）调一次：按上次缓存先把色换好。 */
export function bootTheme(): ThemeCache {
  const c = readCache();
  applyTheme(c.tokens);
  return c;
}
